import { Injectable } from '@angular/core';
import { User } from '@csx/security';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConstant } from '../common/app.constant';
import { ActionModel } from '../models/actionmodel';
import { AuthorityAction } from '../models/authorityaction.enum';
import { AuthorityPointGroup } from '../models/authoritypointgroup';
import { HttpContentType, HttpMethod, HttpRequestOptions, HttpResponse, HttpStatus } from '../models/http';
import { Outage } from '../models/outage';
import { IUserAlert } from '../models/useralert';
import { Source } from '../models/source.enum';
import { Status } from '../models/status.enum';
import { ITrack } from '../models/track';
import { ITrackAuthority, TrackAuthority } from '../models/trackauthority';
import { IUserConfirmation } from '../models/userconfirmation';
import { ATMEventData, EventBusService, EventType } from '../services/eventbus.service';
import { LocalStorageService } from '../services/localstorage.service';
import { DataShareService } from './data-share.service';
import { HttpService } from './http.service';
import { ChangeRequest } from '../models/changerequest';
import { ProxyUser } from '../models/proxyuser';
import { EICSTatus } from '../models/eic-status.enum';
import { SecondaryEICModificationStatus } from '../models/secondary-eic-modification-status.enum';
import { IsharingChangeModel } from '../models/sharing-change-model';

@Injectable()
export class DataService {
  sharedAuthorityModalStatus: Subject<any> = new Subject<any>();
  notificationData: any;
  // mock data, replace when API is ready
  sharedAuthority: ITrackAuthority[] = [];

  constructor(
    private localStorageService: LocalStorageService,
    private eventBus: EventBusService,
    private httpService: HttpService,
    private user: User,
    private dataShareService: DataShareService,
    private proxy: ProxyUser
  ) {
    this.localStorageService.storeUserDetail(this.user);
  }



  openSharedAuthorityModel(status: boolean, type?: String, notificationItem?: any) {
    let item = Object.assign({}, notificationItem);
    let authorityShared = null;
    if (status) {
      if (!item.authority) {
        this.sharedAuthority.forEach(authority => {
          if (item.authorityId === authority.id) {
            authorityShared = authority;
            return;
          }
        });
        if (
          !authorityShared ||
          !authorityShared.ec1Number ||
          (item.messageHeading !== 'Rejected Modification' && authorityShared.authorityChangeRequests.length === 0) ||
          (authorityShared.authorityChangeRequests.length > 0 &&
            authorityShared.authorityChangeRequests.findIndex(
              changeRequest => changeRequest.authorityChangeRequestId === item.authorityChangeRequestId
            ) === -1)
        ) {
          this.eventBus.fireEvent(
            EventType.NOTIFY_FAILURE_EVENT,
            new ATMEventData(null, () => {
              return AppConstant.EXPIRED_NOTIFICATION_TEXT;
            })
          );
          return;
        }
        item.authority = authorityShared;
      }
    }
    this.sharedAuthorityModalStatus.next({ status, type, item });
  }

  returnSharedAuthorityOnId(authority_id) {
    let selectedAuthority = null;
    this.sharedAuthority.forEach(authority => {
      if (authority_id === authority.id) {
        selectedAuthority = authority;
        return;
      }
    });
    if (selectedAuthority) {
      return new TrackAuthority(selectedAuthority, (this.proxy.racf || this.user.racf));
    }
    return null;
  }

  getAuthorityListByMilepostRange(milepost: Number, range: String, prefix: String): Observable<TrackAuthority[]> {
    let request = this.httpService.request({
      method: HttpMethod.Get,
      url: 'v2/track-authorities/sharing?source=Electronic&milepost=' + milepost + '&range=' + range + '&status=A&prefix=' + prefix,
      successMessageOverride: n => null,
      errorMessageOverride: n => 'Authorities List could not be fetched.'
    });
    return request.pipe(
      map(
        response => {
          const authority: TrackAuthority[] = [];
          const responseData = response.responseObject as ITrackAuthority[];
          responseData.forEach((authorities: ITrackAuthority) => {
            let data = new TrackAuthority(authorities, (this.proxy.racf || this.user.racf));
            authority.push(data);
          });
          return authority as TrackAuthority[];
        },
        (error: Error) => {
          throw error;
        }
      )
    );
  }

  transferAuthority(trackAuthority: TrackAuthority, action: Source): Promise<ITrackAuthority> {
    let options: HttpRequestOptions = this.createRequestObjectWithAffirmations(
      'v2/track-authorities/transfer/' + trackAuthority.id,
      'TR',
      trackAuthority
    );

    this.eventBus.fireEvent(EventType.BLOCK_UI_EVENT, null);

    return this.sendRequest(options)
      .then((responseObject: any) => {
        responseObject.transferSuccess = true;
        this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(responseObject, () => { }));
        return responseObject;
      })
      .catch(error => {
        if (error.status === HttpStatus.Offline) {
          let actionModel: ActionModel = {
            options: JSON.stringify(options),
            trackAuthority: trackAuthority,
            authorityAction: AuthorityAction.TRANSFER_TO_PAPER,
            numberOfRetry: 5
          } as ActionModel;

          this.localStorageService
            .enqueueOfflineAction([actionModel])
            .then(() => this.localStorageService.setSourceAuthoritySource(trackAuthority, Source.TRANSFER_TO_PAPER))
            .then(() => {
              trackAuthority.sourceSystemName = Source.TRANSFER_TO_PAPER;
              this.eventBus.fireEvent(
                EventType.NOTIFY_SUCCESS_EVENT,
                new ATMEventData(
                  null,
                  () => 'Offline action has been successfully queued! Will be sent to the Server once connectivity is regained!'
                )
              );
              this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(trackAuthority, () => { }));
            })
            .catch(error =>
              this.eventBus.fireEvent(
                EventType.NOTIFY_FAILURE_EVENT,
                new ATMEventData(null, () => 'Failed to queue the offline action : ' + JSON.stringify(error))
              )
            );
        } else {
          trackAuthority.dirty = false;
          trackAuthority.transferFailed = true;

          trackAuthority.sourceSystemName = Source.TRANSFER_TO_PAPER;
          this.localStorageService
            .setSourceAuthoritySource(trackAuthority, Source.ATAM)
            .then(() => this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(trackAuthority, () => { })));
        }
        console.warn(error);
        throw error;
      });
  }

  updateAuthorityStatus(trackAuthority: TrackAuthority, action: Status): Promise<ITrackAuthority> {
    let options: HttpRequestOptions = this.createRequestObjectWithAffirmations(
      'v2/track-authorities/' + trackAuthority.id + '/status/' + action,
      this.findActionForStatus(action),
      trackAuthority
    );

    this.eventBus.fireEvent(EventType.BLOCK_UI_EVENT, null);

    return this.sendRequest(options).then((responseObject: any) => {
      this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(responseObject, () => { }));
      return responseObject;
    });
  }

  sendRequest(options: HttpRequestOptions): Promise<any> {
    return this.httpService
      .request(options)
      .toPromise()
      .then(response => {
        this.eventBus.fireEvent(EventType.RELEASE_UI_EVENT, new ATMEventData(null, () => { }));
        return response.responseObject;
      })
      .catch(error => {
        this.eventBus.fireEvent(EventType.RELEASE_UI_EVENT, new ATMEventData(null, () => { }));
        throw error;
      });
  }

  setUserNotificationAcknowledgement(
    notificationIdList: number[],
    authority?: TrackAuthority
  ): Promise<void> {
    if (notificationIdList.length <= 0) return;
    let request = this.httpService.request({
      method: HttpMethod.Post,
      headers: this.getHeaders(),
      url: 'v2/notifications',
      body: {
        user: {
          racf: (this.proxy.racf || this.user.racf),
          employeeId: '',
          name: this.user.firstName + ' ' + this.user.lastName,
          qualifiedSubDivisionCodes: []
        },
        notifications: notificationIdList
      }
    });

    return request.toPromise().then(
      response => {
        if (authority) {
          authority.isSharedModificationRejected = false;
          this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(authority, () => { }, true));
        }
      },
      error => { }
    );
  }

  raiseOutage(comments?: string): Promise<void> {
    let payload: string;
    if (comments) {
      payload = comments;
    } else {
      payload = 'Clear Outage';
    }
    let request = this.httpService.request({
      method: HttpMethod.Post,
      headers: this.getHeaders(),
      url: 'v2/notifications/appmaintenance',
      body: {
        'message': payload
      }
    });

    return request.toPromise().then(response => { });
  }

  getAuthorityPointsByPrefixMilepostRange(
    prefix,
    milepost,
    range
  ): Promise<AuthorityPointGroup[]> {
    let request = this.httpService.request({
      method: HttpMethod.Get,
      headers: this.getHeaders(),
      url:
        'v2/authoritypoints/users' +
        '?ProximityPrefix=' +
        prefix +
        '&ProximityMilepost=' +
        milepost +
        '&ProximityRange=' +
        range,
      isUserAction: true,
      timeout: 120000, //2m timeout because the GIS query can take a while
      successMessageOverride: n => null
    });

    return request.toPromise().then(response => {
      return response.responseObject;
    });
  }

  getTracksByPrefixMilepost(prefix, milepost): Promise<ITrack[]> {
    let request = this.httpService.request({
      method: HttpMethod.Get,
      headers: this.getHeaders(),
      url:
        'v2/authoritypoints/tracks?prefix=' +
        prefix +
        '&milepost=' +
        milepost,
      isUserAction: true,
      successMessageOverride: n => null,
      errorMessageOverride: n => 'Unable to retrieve valid tracks at this time. Please reach out to dispatching directly.'
    });

    return request.toPromise().then(response => {
      return response.responseObject;
    });
  }

  postNewAuthority(authority): Promise<void> {
    let request = this.httpService.request({
      method: HttpMethod.Post,
      headers: this.getHeaders(),
      url: 'v2/track-authorities/create',
      isUserAction: true,
      successMessageOverride: n => 'Authority created successfully',
      errorMessageOverride: n => {
        let errorMessage: string = '';

        if (n.responseObject !== undefined && n.responseObject.length !== undefined) {
          for (let i = 0; i < n.responseObject.length; i++) {
            let error = n.responseObject[i];

            //It does not appear that we are returning the error information for validation highlighting... is this something we should consider?
            if (error.field !== undefined && error.message !== undefined) {
              if (errorMessage.length > 0) errorMessage += '\n';
              errorMessage += 'Field "' + error.field + '": ' + error.message;
            }
          }
        }
        return errorMessage.length === 0 ? n.errorMessage : errorMessage;
      },
      body: authority
    });

    return request.toPromise().then(response => {
      this.eventBus.fireEvent(EventType.RESET_POLL_EVENT, new ATMEventData(null, () => ''));
      return response.responseObject;
    });
  }

  addComment(trackAuthority: TrackAuthority, comment: String): Promise<void> {
    let request = this.httpService.request({
      method: HttpMethod.Put,
      headers: this.getHeaders(),
      url: 'v2/track-authorities/' + trackAuthority.id + '/comments',
      isUserAction: true,
      body: {
        racf: this.user.racf,
        text: comment
      },
      errorMessageOverride: n => 'An error occurred while adding the comment.'
    });

    //Successful messages do not reply with any payload
    return request.toPromise().then(response => this.eventBus.fireEvent(EventType.RESET_POLL_EVENT, new ATMEventData(null, () => '')));
  }

  getUserConfirmationList(trackAuthority: TrackAuthority, action: Status | Source): Promise<IUserConfirmation[]> {
    let affirmationAction = this.findActionForStatus(action);
    let request = this.httpService.request({
      method: HttpMethod.Get,
      headers: this.getHeaders(),
      url: `v2/affirmation/${trackAuthority.id}/${affirmationAction}${affirmationAction === 'SA' ? '?racf=' + (this.proxy.racf || this.user.racf) : ''
        }`,
      isUserAction: true,
      successMessageOverride: n => null, //We do not want a success message
      errorMessageOverride: n => null //The consumer may decide to show error message if deemed neccessary.
    });

    return request.toPromise().then(response => {
      return response.responseObject;
    });
  }

  getSignRequest(): Promise<string> {
    let request = this.httpService.request({
      method: HttpMethod.Get,
      headers: this.getHeaders(),
      url: 'v2/signrequest?racf=' + this.user.racf,
      responseContentType: HttpContentType.PLAIN_TEXT
    });

    return request.toPromise().then(response => response.responseObject);
  }

  verifySignResponse(responseToken): Promise<boolean> {
    let request = this.httpService.request({
      method: HttpMethod.Post,
      headers: this.getHeaders(),
      url: 'v2/signrequest/verify',
      body: 'signResponse=' + encodeURIComponent(responseToken),
      requestContentType: HttpContentType.FORM_DATA
    });

    return request.toPromise().then(response => response.status === HttpStatus.Success);
  }

  private findActionForStatus(requestedStatus: Status | Source): string {
    switch (requestedStatus) {
      case Status.RELEASE_USER_APPROVED:
        return 'RA';
      case Status.AUTHORITY_USER_APPROVED:
        return 'CA';
      case Status.RELEASE_REQUESTED:
        return 'RR';
      case Source.TRANSFER_TO_PAPER:
        return 'TR';
      case Status.CHANGE_USER_APPROVED:
        return 'CUA';
      case Status.CHANGE_REQUEST:
        return 'CR';
      case Status.SHARED_AUTHORITY_ACCEPTED:
        return 'CUA';
      case Status.SHARING_AUTHORITY:
        return 'SA';
      case Status.SHARING_RELEASE:
        return 'SR';
    }
    return null;
  }

  private createRequestObjectWithAffirmations(
    requestUrl: string,
    requestConfirmationAction: string,
    trackAuthority: TrackAuthority
  ) {
    if (trackAuthority.status && trackAuthority.status == Status.AUTHORITY_APPROVED) {
      return {
        method: HttpMethod.Post,
        headers: this.getHeaders(),
        url: requestUrl,
        isUserAction: true,
        successMessageOverride: n => 'Request sent',
        body: {
          user: {
            racf: (this.proxy.racf || this.user.racf),
            employeeId: '',
            name: this.user.firstName + ' ' + this.user.lastName,
            qualifiedSubDivisionCodes: []
          },
          confirmationAction: requestConfirmationAction,
          confirmationMessageList: trackAuthority.userConfirmationList
        }
      };
    }
    return {
      method: HttpMethod.Post,
      headers: this.getHeaders(),
      url: requestUrl,
      isUserAction: true,
      successMessageOverride: n => 'Request sent for EC1: #' + trackAuthority.ec1Number,
      body: {
        user: {
          racf: (this.proxy.racf || this.user.racf),
          employeeId: '',
          name: this.user.firstName + ' ' + this.user.lastName,
          qualifiedSubDivisionCodes: []
        },
        confirmationAction: requestConfirmationAction,
        confirmationMessageList: trackAuthority.userConfirmationList
      }
    };
  }

  updateChangeRequestStatus(trackAuthority: TrackAuthority, action: Status): Promise<TrackAuthority> {
    let authorityChangeRequest = trackAuthority.modifyReportbyChangeRequest;
    if (authorityChangeRequest === null) {
      return;
    }
    let options: HttpRequestOptions = this.createChangeRequestObjectWithAffirmations(
      'v2/track-authorities/' +
      trackAuthority.id +
      '/change-requests/' +
      authorityChangeRequest.authorityChangeRequestId +
      '/status',
      action,
      trackAuthority
    );
    this.eventBus.fireEvent(EventType.BLOCK_UI_EVENT, null);
    return this.sendRequest(options).then(
      (responseObject: any) => {
        this.dataShareService.setModifyOrReportByChangeRequestStatus(trackAuthority, action.toString());
        this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(trackAuthority, () => { }));
        return responseObject;
      },
      err => { }
    );
  }

  private createChangeRequestObjectWithAffirmations(requestUrl: string, action: Status, trackAuthority: TrackAuthority) {
    if (action === Status.CHANGE_USER_APPROVED) {
      let userConfirmationList: Array<IUserConfirmation> = new Array<IUserConfirmation>();
      userConfirmationList = trackAuthority.userConfirmationList;
      return {
        method: HttpMethod.Post,
        url: requestUrl,
        headers: this.getHeaders(),
        isUserAction: true,
        successMessageOverride: n => 'Request sent for EC1: #' + trackAuthority.ec1Number,
        errorMessageOverride: n => {
          let errorMessage = 'Change Request for Track Authority ' + trackAuthority.ec1Number + ' failed';

          if (n.responseObject !== undefined && n.responseObject.length !== undefined) {
            errorMessage += ': ';
            for (let i = 0; i < n.responseObject.length; i++) {
              let error = n.responseObject[i];
              if (error.field !== undefined && error.message !== undefined) {
                if (errorMessage.length > 0) {
                  errorMessage += '\n';
                }
                errorMessage += 'Field "' + error.field + '": ' + error.message;
              }
            }
          }
          return errorMessage.length === 0 ? n.errorMessage : errorMessage;
        },
        body: {
          status: action,
          confirmationAction: this.findActionForStatus(action),
          confirmation: userConfirmationList
        }
      };
    } else if (action === Status.CHANGE_USER_REJECTED) {
      return {
        method: HttpMethod.Post,
        url: requestUrl,
        headers: this.getHeaders(),
        isUserAction: true,
        successMessageOverride: n => 'Request sent for EC1: #' + trackAuthority.ec1Number,
        errorMessageOverride: n => {
          let errorMessage = 'Change Request for Track Authority ' + trackAuthority.ec1Number + ' failed';

          if (n.responseObject !== undefined && n.responseObject.length !== undefined) {
            errorMessage += ': ';
            for (let i = 0; i < n.responseObject.length; i++) {
              let error = n.responseObject[i];
              if (error.field !== undefined && error.message !== undefined) {
                if (errorMessage.length > 0) {
                  errorMessage += '\n';
                }
                errorMessage += 'Field "' + error.field + '": ' + error.message;
              }
            }
          }
          return errorMessage.length === 0 ? n.errorMessage : errorMessage;
        },
        body: {
          status: action
        }
      };
    }
  }

  changeRequest(changeRequest, changeRequestBody, trackAuthority: TrackAuthority): Promise<any> {
    let request = this.httpService.request({
      method: HttpMethod.Post,
      url:
        'v2/track-authorities/' +
        changeRequest.id +
        '/change-requests/' +
        changeRequest.authorityChangeRequestId +
        '/status',
      headers: this.getHeaders(),
      body: changeRequestBody,
      successMessageOverride: n => null,
      errorMessageOverride: n => AppConstant.ERROR_IN_CHANGE_REQUEST_API
    });
    return request
      .toPromise()
      .then(response => {
        this.updateStatuses(trackAuthority, changeRequest.authorityChangeRequestId, changeRequestBody.status);
        this.eventBus.fireEvent(
          EventType.RESET_POLL_EVENT,
          new ATMEventData(null, () => '')
        );
        return response.responseObject as ITrackAuthority;
      })
      .catch(error => {
        throw error;
      });
  }

  private updateStatuses(trackAuthority: TrackAuthority, changeRequestId: number, status: string) {
    // Update change Request status
    trackAuthority.authorityChangeRequests.forEach(item => {
      if (item.authorityChangeRequestId === changeRequestId) {
        item.authorityChangeRequestStatus = status;
        this.updateEicStatus(status, trackAuthority, item.sharingChangeModel);
      }
    });
  }

  private updateEicStatus(status: string, trackAuthority: TrackAuthority, sharingChangeModel: IsharingChangeModel) {
    if (status === Status.SHARING_REQUEST_REJECTED.toString()) {
      trackAuthority.employeeInCharges.forEach(item => {
        if (item.eicUserId === sharingChangeModel.secondaryEICRacf) {
          item.status = EICSTatus.REJECTED;
        }
      });
    } else if (status === Status.CHANGE_PROPOSED_PARTIALLY_ACCEPTED.toString()) {
      trackAuthority.employeeInCharges.forEach(item => {
        if (item.eicUserId === trackAuthority.loggedInEICDetail.eicUserId) {
          item.lastChangeRequestStatus = SecondaryEICModificationStatus.ACCEPTED.toString();
        }
      });
    } else if (status === Status.CHANGE_PROPOSED_REJECTED.toString()) {
      trackAuthority.employeeInCharges.forEach(item => {
        if (item.eicUserId === trackAuthority.loggedInEICDetail.eicUserId) {
          item.lastChangeRequestStatus = '';
        }
      });
      trackAuthority.authorityChangeRequests = [];
    }
    else if (status === Status.CHANGE_PROPOSED_CANCELED.toString()) {
      trackAuthority.authorityChangeRequests = [];
    }
    else if (status === Status.SHARING_INITIATE_REJECTED.toString() || status === Status.UNSHARE_ACCEPTED.toString()) {
      trackAuthority.status = Status.AUTHORITY_HIDDEN;
    }
  }

  getAuthorityModificationByChangeRequest(trackAuthorityId: number, changeRequestId: number): Observable<any> {
    let request = this.httpService.request({
      method: HttpMethod.Get,
      url: `v2/track-authorities/${trackAuthorityId}/change-requests/${changeRequestId}`,
      successMessageOverride: n => null,
      errorMessageOverride: n => AppConstant.ERROR_GET_CHANGE_REQUEST_FAILED
    });
    return request.pipe(
      map(
        response => {
          return response.responseObject;
        },
        (error: Error) => {
          throw error;
        }
      )
    );
  }

  getHeaders() {
    let headers;
    if (this.proxy.racf !== undefined) {
      return headers = { 'proxiedRacf': this.proxy.racf }
    }
    return headers;
  }

  getUserResource(): Promise<Outage[]> {
    let request = this.httpService.request(
      {
        method: HttpMethod.Get,
        headers: this.getHeaders(),
        url:
          'v2/userappdata?source=Electronic',
        isUserAction: true,
        successMessageOverride: n => null,
        errorMessageOverride: n => 'Authority list could not be updated.'

      }
    );
    return request.toPromise().then(response => {
      this.sharedAuthority = response.responseObject.trackAuthorityList as ITrackAuthority[];
      return response.responseObject;

    })
  }

  getUserAlert(): Promise<IUserAlert | null> {
    let request = this.httpService.request(
      {
        method: HttpMethod.Get,
        headers: this.getHeaders(),
        url: 'v2/user-alert',
        successMessageOverride: n => null,
        errorMessageOverride: n => null
      }
    );

    return request.toPromise().then(response => {
      if (!response) {
        return null;
      }

      return response.responseObject as IUserAlert;
    }).catch(error => {
      if (error && error.statusCode === 404) {
        console.warn("no active user alert found");
      }
      console.warn(error);
      return null;
    });
  }

}
